// Pinch-scale snapping (2026-09-07; per-wheel ladder 2026-10-05).
//
// The pinch no longer applies a continuous multiplier: the wheel locks
// onto whole-inch diameters between 9" and 30", stepping 1" at a time,
// and the header pill reads the same number. This module is the pure
// projection from a continuous pinch multiplier onto that ladder; the
// per-wheel accumulator that FEEDS it lives in the placement
// controller, because the accumulator is stateful and this is not.

export const MIN_SNAPPED_DIAMETER_IN = 9
export const MAX_SNAPPED_DIAMETER_IN = 30

export interface PinchSnapResult {
  /** The continuous multiplier after clamping to the ladder's ends.
   *  Clamped so residual pinching past a bound does not pile up and
   *  make the way back feel dead. */
  readonly continuousMultiplier: number
  /** The whole-inch diameter the pinch currently lands on. */
  readonly snappedDiameterInches: number
  /** The multiplier that renders exactly that diameter. */
  readonly snappedMultiplier: number
}

/**
 * Projects a continuous pinch multiplier onto the whole-inch ladder.
 *
 * Returns null when the base diameter is degenerate (no payload size,
 * zero, negative, NaN) — the caller falls back to the continuous
 * behaviour, because without a base there is no inch to snap to.
 */
export function snapPinchScale({
  baseDiameterInches,
  continuousMultiplier,
  ladder,
}: {
  baseDiameterInches: number
  continuousMultiplier: number
  /** The diameters the pinch may land on (see snapLadder). Omitted, it is
   *  every whole inch from 9" to 30" — the 2026-09-07 behaviour. */
  ladder?: readonly number[]
}): PinchSnapResult | null {
  if (!Number.isFinite(baseDiameterInches) || baseDiameterInches <= 0) {
    return null
  }
  const rungs =
    ladder != null && ladder.length > 0
      ? [...ladder].sort((a, b) => a - b)
      : wholeInches(MIN_SNAPPED_DIAMETER_IN, MAX_SNAPPED_DIAMETER_IN)
  const lowest = rungs[0]!
  const highest = rungs[rungs.length - 1]!
  const clamped = Math.max(
    lowest / baseDiameterInches,
    Math.min(highest / baseDiameterInches, continuousMultiplier),
  )
  const target = clamped * baseDiameterInches
  // The NEAREST rung, so a ladder with gaps (20, 18, 17, 15) steps from
  // size to size and never renders a diameter the wheel is not made in.
  const snappedDiameterInches = rungs.reduce((best, rung) =>
    Math.abs(rung - target) < Math.abs(best - target) ? rung : best,
  )
  return {
    continuousMultiplier: clamped,
    snappedDiameterInches,
    snappedMultiplier: snappedDiameterInches / baseDiameterInches,
  }
}

/** Half-span of the ladder around the current size when the payload
 *  declares no size range (2026-10-05, developer's spec). */
export const UNDECLARED_LADDER_HALF_SPAN_IN = 3

/**
 * The diameters a wheel's pinch may land on.
 *
 * A declared RANGE — two or more distinct diameters on the payload's size
 * list — is the ladder exactly: those are the sizes the wheel is made in
 * (WELD S70: 20, 18, 17, 15). Anything less is not a range, so the ladder
 * is the current size +/-3 whole inches, inside 9"-30".
 */
export function snapLadder({
  baseDiameterInches,
  sizeDiameters,
  halfSpanInches = UNDECLARED_LADDER_HALF_SPAN_IN,
}: {
  baseDiameterInches: number
  sizeDiameters: readonly number[]
  /** The +/- span when no range is declared. An ESTIMATED size passes
   *  ?use_estimated_size_snap_range here (0 = the current size only). */
  halfSpanInches?: number
}): number[] {
  const declared = [...new Set(sizeDiameters.filter((d) => Number.isFinite(d) && d > 0))]
  if (declared.length >= 2) return declared.sort((a, b) => a - b)
  const current = Math.round(baseDiameterInches)
  return wholeInches(
    Math.max(MIN_SNAPPED_DIAMETER_IN, current - halfSpanInches),
    Math.min(MAX_SNAPPED_DIAMETER_IN, current + halfSpanInches),
  )
}

function wholeInches(from: number, to: number): number[] {
  const rungs: number[] = []
  for (let d = from; d <= to; d += 1) rungs.push(d)
  return rungs
}
